// #genai Single-route sign-in flow for shared resources.
//
// Steps
// ─────
// loading      – validateResourcePath & requestPreAccessToken in-flight
// notShared    – resource is NOT a guest flow → show standard Okta login button
// email        – ask user for their email address
// guestChoice  – email is not an EV user → offer "Create account" or "Continue as guest"
// verifyEmail  – guest sign-in email was sent; pollEmailVerification is active
//
// Bootstrap
// ─────────
// Normally SharedStartupGate calls validateResourcePath & requestPreAccessToken before
// redirecting here, and the results live in the GuestAccess Redux slice via sessionStorage
// hydration. When the user lands directly on /signin?redirect=... (e.g. deep link, back
// button, or page refresh), Redux is empty.  In that case this component self-bootstraps:
// it parses the redirect URL, calls the same two APIs, and populates Redux itself.
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@eagleview/ev-comp-library';
import AuthSplitLayout, { SplitLayoutFooter } from 'features/auth-split-layout';
import {
  isTokenValid, clearExpiredToken, redirectEvUserToSignIn, redirectToOkta, isValidEmail,
} from 'utils/app.utils';
import { SHARED_LOGIN_ID_KEY } from 'constants/AppConstants';
import {
  validateResourcePathService,
  requestPreAccessTokenService,
  storePreAccessToken,
  checkIsEvUserService,
  sendEmailVerificationService,
  processBootstrapResults,
  extractVerificationId,
} from 'services/GuestAccess.service';
import {
  setGuestResource,
  setGuestUserInfo,
  setGuestVerification,
  setGuestFlowPhase,
  setGuestError,
} from 'features/guest-access/GuestAccess.actions';
import useGuestVerificationPolling from 'features/guest-access/useGuestVerificationPolling';
import {
  LoadingStep, NotSharedStep, EmailStep, GuestChoiceStep, VerifyEmailStep,
} from './SignInSteps';
import signInFlowStyles from './SignInFlow.styles';
import {
  ACCESS_PHASES,
  SIGN_IN_STEP as STEP,
  RESEND_STATUS,
  GUEST_ROUTES,
  GUEST_ERROR_MESSAGES,
  POLLING_CONFIG,
} from '../../constants/GuestAccess';

// #genai Format remaining ms as M:SS for link-expiry countdown.
const formatCountdown = (ms) => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ─── Bootstrap helpers ────────────────────────────────────────────────────────

// #genai
const parseRedirectForResource = (redirectUrl) => {
  try {
    const url = redirectUrl.startsWith('http')
      ? new URL(redirectUrl)
      : new URL(redirectUrl, window.location.origin);
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length < 2) return null;
    return {
      appCode: segments[0],
      resourcePath: segments.join('/'),
    };
  } catch (_) {
    return null;
  }
};

const useStyles = makeStyles(signInFlowStyles);

// ─── Component ────────────────────────────────────────────────────────────────

const SignInFlow = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirect = query.get('redirect') || GUEST_ROUTES.DEFAULT_REDIRECT;

  // GuestAccess Redux state (hydrated from sessionStorage by SharedStartupGate)
  const {
    isResourceShared,
    isPreAccessTokenOk,
    verificationId,
    loginId: storedLoginId,
    guestFlowPhase,
  } = useSelector((state) => state.GuestAccess);

  // true when BOTH validateResourcePath & requestPreAccessToken confirmed sharing
  const isGuestFlow = isResourceShared && isPreAccessTokenOk;

  const [step, setStep] = useState(STEP.loading);
  const [email, setEmail] = useState(query.get('email') || '');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [resendStatus, setResendStatus] = useState(RESEND_STATUS.SENT);
  // #genai True when the 10-minute polling window expires; shows "Link Expired" UI.
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  // #genai Countdown timer for "Link will expire in M:SS" on verify-email step.
  const [linkExpiresAt, setLinkExpiresAt] = useState(null);
  const [countdownLabel, setCountdownLabel] = useState('10:00');
  // #genai Mirror verificationId in local state so the polling hook gets the value
  // in the same render as setStep(STEP.verifyEmail), avoiding a Redux timing gap.
  const [activeVerificationId, setActiveVerificationId] = useState(verificationId || '');
  const [bootstrapping, setBootstrapping] = useState(false);
  const bootstrappedRef = useRef(false); // prevent duplicate bootstrap on re-renders
  // #genai Resend button cooldown (seconds). Disabled for 30s after a successful resend.
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);

  // Tied to activeVerificationId (not step) so polling fires in the same render
  // as setActiveVerificationId(). Cleared to '' by "Go Back" to stop polling.
  const isPollingActive = Boolean(activeVerificationId);
  useGuestVerificationPolling({
    verificationId: activeVerificationId,
    isActive: isPollingActive,
    redirectTo: redirect,
  });

  // If already authenticated with a valid (non-expired) token, skip sign-in.
  useEffect(() => {
    if (isTokenValid()) {
      if (redirect.startsWith('http://') || redirect.startsWith('https://')) {
        window.location.href = redirect;
      } else {
        history.replace(redirect);
      }
    } else {
      clearExpiredToken();
    }
  }, [redirect, history]);

  // Self-bootstrap: call validateResourcePath & requestPreAccessToken when Redux is empty.
  useEffect(() => {
    if (isGuestFlow || bootstrappedRef.current) return;
    const parsed = parseRedirectForResource(redirect);
    if (!parsed) return; // no resource path → let step resolution show notShared
    bootstrappedRef.current = true;
    setBootstrapping(true);
    const { appCode, resourcePath } = parsed;
    Promise.all([
      validateResourcePathService(appCode, resourcePath),
      requestPreAccessTokenService(appCode, resourcePath),
    ]).then(([validationResult, preAccessResult]) => {
      const payload = processBootstrapResults(validationResult, preAccessResult, appCode, resourcePath);
      if (payload.preAccessToken) storePreAccessToken(payload.preAccessToken);
      dispatch(setGuestResource(payload));
      dispatch(setGuestFlowPhase(payload.isResourceShared ? ACCESS_PHASES.EMAIL_INPUT : ACCESS_PHASES.LEGACY));
    }).catch(() => {
      dispatch(setGuestFlowPhase(ACCESS_PHASES.LEGACY));
    }).finally(() => {
      setBootstrapping(false);
    });
  }, []); // intentionally run once on mount only

  // Resolve initial step once bootstrap completes.
  useEffect(() => {
    if (bootstrapping) return; // wait for self-bootstrap to complete
    setStep(isGuestFlow ? STEP.email : STEP.notShared);
  }, [isGuestFlow, bootstrapping]);

  // React to polling hook phase changes (timeout). #genai
  useEffect(() => {
    if (guestFlowPhase === ACCESS_PHASES.ERROR) {
      setIsLinkExpired(true);
    }
  }, [guestFlowPhase]);

  // #genai Countdown timer: update "Link will expire in M:SS" every second on verify-email step.
  useEffect(() => {
    if (step !== STEP.verifyEmail || !linkExpiresAt || isLinkExpired) return undefined;
    let intervalId;
    const tick = () => {
      const remaining = linkExpiresAt - Date.now();
      if (remaining <= 0) {
        if (intervalId) clearInterval(intervalId);
        setCountdownLabel('0:00');
        setIsLinkExpired(true);
        return;
      }
      setCountdownLabel(formatCountdown(remaining));
    };
    tick(); // run once immediately
    intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [step, linkExpiresAt, isLinkExpired]);

  // #genai Resend cooldown: decrement every second when > 0.
  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const id = setInterval(() => {
      setResendCooldownSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldownSeconds]);

  // ── checkIsEvUser: check if the email belongs to an EV user ─────────────────
  const onContinue = async (evt) => {
    evt.preventDefault();
    setError('');
    const normalized = (email || '').trim();
    if (!isValidEmail(normalized)) {
      setError(GUEST_ERROR_MESSAGES.INVALID_EMAIL);
      return;
    }

    try {
      const { ok, json = {} } = await checkIsEvUserService(normalized);
      if (!ok) {
        setError(GUEST_ERROR_MESSAGES.GENERIC_ERROR);
        return;
      }

      const { isEvUser, loginId } = json;
      dispatch(setGuestUserInfo({ isEvUser, loginId: loginId || normalized, email: normalized }));
      dispatch(setGuestFlowPhase(ACCESS_PHASES.GUEST_CHOICE));

      if (isEvUser) {
        // Persist loginId so SharedStartupGate can fire the user-access-audit call
        // after the Okta redirect. Consumed once and removed — refresh won't re-trigger.
        try { sessionStorage.setItem(SHARED_LOGIN_ID_KEY, loginId || normalized); } catch (_) { /* noop */ }
        // EV user → redirect to Okta sign-in with email pre-filled via login_hint. #genai
        redirectEvUserToSignIn(redirect, normalized);
        return;
      }

      setEmail(normalized);
      setStep(STEP.guestChoice);
    } catch (_) {
      setError(GUEST_ERROR_MESSAGES.GENERIC_ERROR);
    }
  };

  // ── sendEmailVerification ──────────────────────────────────────────────────
  const continueAsGuest = async () => {
    setError('');
    setSending(true);
    try {
      const loginId = storedLoginId || email;
      const { ok, json = {} } = await sendEmailVerificationService(loginId);
      if (!ok) {
        setError(GUEST_ERROR_MESSAGES.SEND_FAILED);
        return;
      }
      // sendEmailVerification returns { emailVerificationId: "..." } — used as the polling ID.
      const emailVerificationId = extractVerificationId(json);
      dispatch(setGuestVerification({ verificationId: emailVerificationId, emailSent: true }));
      dispatch(setGuestFlowPhase(ACCESS_PHASES.VERIFY_EMAIL));
      dispatch(setGuestError(null));
      setResendStatus(RESEND_STATUS.SENT);
      setActiveVerificationId(emailVerificationId);
      setLinkExpiresAt(Date.now() + POLLING_CONFIG.MAX_POLL_DURATION_MS);
      setStep(STEP.verifyEmail);
    } catch (_) {
      setError(GUEST_ERROR_MESSAGES.SEND_FAILED);
    } finally {
      setSending(false);
    }
  };

  // ── Resend (re-calls sendEmailVerification) ─────────────────────────────────
  const resend = async () => {
    setResendStatus(RESEND_STATUS.RESENDING);
    try {
      const loginId = storedLoginId || email;
      const { ok, json = {} } = await sendEmailVerificationService(loginId);
      if (ok) {
        const emailVerificationId = extractVerificationId(json);
        if (emailVerificationId) {
          dispatch(setGuestVerification({ verificationId: emailVerificationId, emailSent: true }));
          setActiveVerificationId(emailVerificationId);
          setLinkExpiresAt(Date.now() + POLLING_CONFIG.MAX_POLL_DURATION_MS);
        }
        setIsLinkExpired(false);
        setResendStatus(RESEND_STATUS.SENT);
        setResendCooldownSeconds(30);
      } else {
        setResendStatus(RESEND_STATUS.ERROR);
      }
    } catch (_) {
      setResendStatus(RESEND_STATUS.ERROR);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <AuthSplitLayout footer={<SplitLayoutFooter />} sidebarWidth="509px">
      {step === STEP.loading && <LoadingStep classes={classes} />}
      {step === STEP.notShared && (
        <NotSharedStep
          classes={classes}
          onLogin={() => redirectToOkta(redirect)}
          onGoBack={() => history.goBack()}
        />
      )}
      {step === STEP.email && (
        <EmailStep
          classes={classes}
          email={email}
          setEmail={setEmail}
          onContinue={onContinue}
          isEmailValid={isValidEmail(email)}
          error={error}
        />
      )}
      {step === STEP.guestChoice && (
        <GuestChoiceStep
          classes={classes}
          onContinueAsGuest={continueAsGuest}
          sending={sending}
          error={error}
          onGoBack={() => { setError(''); setStep(STEP.email); }}
        />
      )}
      {step === STEP.verifyEmail && (
        <VerifyEmailStep
          classes={classes}
          isLinkExpired={isLinkExpired}
          countdownLabel={countdownLabel}
          email={email}
          resendStatus={resendStatus}
          resendCooldownSeconds={resendCooldownSeconds}
          onResend={resend}
          onGoBack={() => { setError(''); setActiveVerificationId(''); setLinkExpiresAt(null); setResendCooldownSeconds(0); setStep(STEP.guestChoice); }}
        />
      )}
    </AuthSplitLayout>
  );
};

export default SignInFlow;
